import React from 'react';

import { AppIcons, FeaturedIcon } from '@backrs_ui/common-ui';

import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';

import { Box, SxProps, Theme, Typography } from '@mui/material';

import { GuideMarkdown } from './GuideMarkdown';

export type GuideCalloutVariant = 'info' | 'caution' | 'highlight' | 'positive';

export type GuideCalloutProps = Readonly<{
  variant: GuideCalloutVariant;
  title: string;
  /** Markdown body copy. */
  body: string;
}>;

const VARIANT_ICONS: Record<GuideCalloutVariant, AppIcons> = {
  info: 'Lightbulb05',
  caution: 'AlertTriangle',
  highlight: 'Stars01',
  positive: 'CheckCircle',
};

const VARIANT_ICON_COLORS: Record<GuideCalloutVariant, ColorToken> = {
  info: 'Colors/Text/text-primary',
  caution: 'Colors/Text/text-warning-primary',
  highlight: 'Colors/Text/text-primary',
  positive: 'Colors/Text/text-success-primary',
};

/** Boxed aside for a fact, caution or closing thought inside a guide. */
export const GuideCallout: React.FC<GuideCalloutProps> = ({ variant, title, body }) => (
  <Box sx={calloutSx}>
    <Box sx={headerSx}>
      <FeaturedIcon
        iconName={VARIANT_ICONS[variant]}
        size="md"
        color={VARIANT_ICON_COLORS[variant]}
        border
      />
      <Typography variant="text-md-semibold">{title}</Typography>
    </Box>
    <GuideMarkdown variant="text-sm-regular">{body}</GuideMarkdown>
  </Box>
);

const calloutSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
  p: backrs.spacing['spacing-2xl'],
  borderRadius: backrs.radius['radius-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  backgroundColor: backrs.colors['Colors/Base/White'],
  color: backrs.colors['Colors/Text/text-primary'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});
